import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Briefcase, Loader2, Cpu, Truck, XCircle, Hash, ArrowRight } from "lucide-react";

const STAGE = {
  config_pending: { label: "Config Pending", cls: "text-[#EAE04A] border-[#EAE04A]/30 bg-[#EAE04A]/10", icon: Cpu },
  delivery_pending: { label: "Ready for Delivery", cls: "text-[#00FF41] border-[#00FF41]/30 bg-[#00FF41]/10", icon: Truck },
  config_failed: { label: "Config Failed", cls: "text-[#FF3B30] border-[#FF3B30]/30 bg-[#FF3B30]/10", icon: XCircle },
};

function fmt(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString("en-AU", { timeZone: "Australia/Sydney", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZoneName: "short" }); }
  catch { return "—"; }
}

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/jobs").then(({ data }) => setJobs(data)).catch(() => toast.error("Failed to load jobs")).finally(() => setLoading(false));
  }, []);

  return (
    <div className="p-8 lg:p-10">
      <div className="border-b border-white/10 pb-6 mb-8">
        <div className="text-xs tracking-[0.2em] uppercase text-[#484F58] mb-2 font-mono-jb">Pipeline</div>
        <h1 className="font-mono-jb text-3xl sm:text-4xl font-extrabold tracking-tighter text-white flex items-center gap-3">
          <Briefcase className="text-[#00F0FF]" size={30} /> Jobs
        </h1>
        <p className="text-[#8B949E] mt-3 leading-relaxed">Won opportunities flow here as config jobs for netconfig-gen. When a config passes testing, the job returns as "Ready for Delivery".</p>
      </div>

      {loading ? (
        <div className="font-mono-jb text-[#8B949E] text-sm"><Loader2 className="animate-spin inline mr-2" size={15} /> Loading...</div>
      ) : jobs.length === 0 ? (
        <div className="border border-dashed border-white/10 bg-[#0B0C10] p-12 text-center" data-testid="jobs-empty">
          <Briefcase className="mx-auto text-[#484F58] mb-4" size={44} />
          <div className="font-mono-jb text-lg text-white mb-1">No jobs yet</div>
          <p className="text-[#8B949E] text-sm max-w-md mx-auto">Mark a pre-sales opportunity as WON to create its config job with a PS Code.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="jobs-list">
          {jobs.map((j) => {
            const st = STAGE[j.stage] || { label: j.stage, cls: "text-[#8B949E] border-white/20 bg-white/5", icon: Cpu };
            const Icon = st.icon;
            return (
              <div key={j.id} data-testid={`job-row-${j.ps_code}`} className="flex items-center gap-4 border border-white/10 bg-[#0B0C10] p-4 hover:border-[#00F0FF]/30 transition-colors duration-200">
                <div className="flex items-center gap-1.5 font-mono-jb text-sm text-[#00FF41] w-40 shrink-0" data-testid={`job-pscode-${j.ps_code}`}>
                  <Hash size={14} /> {j.ps_code}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-mono-jb text-sm text-white truncate">{j.client || "—"}</div>
                  <div className="text-[11px] text-[#8B949E] font-mono-jb truncate">{j.project_name} · created {fmt(j.created_at)}</div>
                  {j.result?.note && <div className="text-[10px] text-[#484F58] font-mono-jb mt-0.5 truncate">netconfig-gen: {j.result.note}</div>}
                </div>
                <span data-testid={`job-stage-${j.ps_code}`} className={`flex items-center gap-1.5 font-mono-jb text-[10px] uppercase tracking-wider border rounded-sm px-2.5 py-1.5 shrink-0 ${st.cls}`}>
                  <Icon size={13} /> {st.label}
                </span>
                <Link to={`/project/${j.delivery_project_id || j.project_id}`} className="text-[#484F58] hover:text-[#00F0FF] transition-colors duration-200 shrink-0"><ArrowRight size={18} /></Link>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
